import { createPinia } from 'pinia';
import { Lang, Router } from './core';

if (!window.Wisp) throw new Error('Injectable data missing???');

import './bootstrap/assets';
import './bootstrap/fontawesome';
import './bootstrap/router';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { createApp, defineAsyncComponent } from 'vue';
import { Clipboard, FloatingTooltip } from '~/directives';
import Accordion from './components/Accordion.vue';
import Addon from './components/Addon.vue';
import Alert from './components/Alert.vue';
import Avatar from './components/Avatar.vue';
import Can from './components/Can.vue';
import Date from './components/Date.vue';
import Footer from './components/Footer.vue';
import Paginator from './components/Paginator.vue';
import Skeleton from './components/Skeleton.vue';
import SkeletonContext from './components/SkeletonContext.vue';
import Stepper from './components/Stepper.vue';
import TranslatedMessage from './components/TranslatedMessage.vue';
import Form from './components/forms/Form.vue';
import FormFile from './components/forms/FormFile.vue';
import FormImage from './components/forms/FormImage.vue';
import FormInput from './components/forms/FormInput.vue';
import FormRadioGroup from './components/forms/FormRadioGroup.vue';
import FormRawValue from './components/forms/FormRawValue.vue';
import FormSearch from './components/forms/FormSearch.vue';
import FormSwitch from './components/forms/FormSwitch.vue';
import FormTextarea from './components/forms/FormTextarea.vue';
import Button from './components/ui/Button.vue';
import App from './views/App.vue';

const Chart = defineAsyncComponent(() => import('./components/Chart.vue'));
const Container = defineAsyncComponent(() => import('./components/Container.vue'));
const DataTable = defineAsyncComponent(() => import('./components/DataTable.vue'));
const FileManager = defineAsyncComponent(() => import('./components/files/FileManager.vue'));
const FloatingDropdown = defineAsyncComponent(() => import('./components/FloatingDropdown.vue'));
const List = defineAsyncComponent(() => import('./components/List.vue'));
const Modal = defineAsyncComponent(() => import('./components/Modal.vue'));
const FormEditor = defineAsyncComponent(() => import('./components/forms/FormEditor.vue'));
const FormModelSelect = defineAsyncComponent(() => import('./components/forms/FormModelSelect.vue'));
const FormSelect = defineAsyncComponent(() => import('./components/forms/FormSelect.vue'));

const app = createApp(App);
const pinia = createPinia();

app.use(Router)
    .use(Lang)
    .use(pinia)
    .directive('clipboard', Clipboard)
    .directive('floating-tooltip', FloatingTooltip)
    .component('fa', FontAwesomeIcon)
    .component('accordion', Accordion)
    .component('alert', Alert)
    .component('avatar', Avatar)
    .component('chart', Chart)
    .component('can', Can)
    .component('addon', Addon)
    .component('container', Container)
    .component('data-table', DataTable)
    .component('date', Date)
    .component('file-manager', FileManager)
    .component('list', List)
    .component('modal', Modal)
    .component('paginator', Paginator)
    .component('v-button', Button)
    .component('v-footer', Footer)
    .component('v-form', Form)
    .component('v-form-value', FormRawValue)
    .component('v-editor', FormEditor)
    .component('v-image', FormImage)
    .component('v-file', FormFile)
    .component('v-input', FormInput)
    .component('v-raw-value', FormRawValue)
    .component('v-radio-group', FormRadioGroup)
    .component('v-search', FormSearch)
    .component('v-select', FormSelect)
    .component('v-model-select', FormModelSelect)
    .component('v-switch', FormSwitch)
    .component('v-textarea', FormTextarea)
    .component('skeleton', Skeleton)
    .component('skeleton-context', SkeletonContext)
    .component('stepper', Stepper)
    .component('t', TranslatedMessage)
    .component('floating-dropdown', FloatingDropdown)
    .mount('#app');

// Initialize Pinia-dependent plugins after app is mounted
import { initializePageTitle } from './bootstrap/router';
initializePageTitle();

void import('./plugins/upload').then(({ initializeUploadErrorHandler }) => initializeUploadErrorHandler());
void import('./bootstrap/daemon').then(({ initializeDaemonConnection }) => initializeDaemonConnection(Router));

if (import.meta.env.PROD) {
    void import('./bootstrap/sentry').then(({ enableSentry }) => enableSentry(app, Router));
}

// Navigation is now handled directly by the router bootstrap
